'use client'

import { Spinner } from "@components/atoms";
import DateInput from "@components/atoms/DateInput";
import LeadFormDisclaimer from "@components/atoms/EthinosHomeLeft/LeadFormDisclaimer";
import PhoneInput from "@components/atoms/PhoneInput";
import { getNYearsBack } from "@components/hooks";
import { generateId } from "../LeadForm/helper";
import { Radio, Select, Text, TextInput } from "@mantine/core";
import { DatePickerInput } from '@mantine/dates';
import dayjs from "dayjs";
import React, { useMemo, useState, useEffect } from "react";
import classes from "styles/LeadFormStyles/LeadForm.module.css";
import radioClasses from "styles/LeadFormStyles/RadioForm.module.css";

export interface FiveLeadFormProps {
    /** A unique name for the form to prefix element IDs. */
    formName: string;
    isds: Record<string, string>[];
    getQuoteForm: any;
    onMobileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDateChangeMobile?: (value: string) => void;
    onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMobileReset: () => void;
    onViewPlan: () => void;
    initData: any;
    constraint: any;
    isLoading: boolean;
    onCountryChange: (val: string) => void;
    onCityChange: (val: string) => void;
    cities: any;
    errors: {
        phone?: string;
        name?: string;
        otp?: string;
        dob?: string;
        city?: string;
        port?: string;
    };
    onPortChange: (value: string) => void;
}

const capitalizeFirst = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const FiveLeadForm: React.FC<FiveLeadFormProps> = ({
    formName,
    isds,
    cities,
    getQuoteForm,
    onMobileChange,
    onMobileReset,
    onPortChange,
    onCountryChange,
    initData,
    errors,
    isLoading,
    onViewPlan,
    onNameChange,
    onDateChange,
    onDateChangeMobile,
    constraint,
    onCityChange
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const defaultCountry = "India";
    
    const isFormInvalid = () => {
        const { phoneNumber, name, dob, city, port } = getQuoteForm || {};
        return !phoneNumber || !name?.trim() || !dob || !city || !port || !!errors.phone || !!errors.name || !!errors.dob || !!errors.city || !!errors.port;
    };

    const capitalizedCityOptions = useMemo(() => {
        const cityList = cities?.cities || [];

        return [...cityList]
            .sort((a: string, b: string) => a.localeCompare(b))
            .map((city: string) => ({
                value: city.toLowerCase(),
                label: capitalizeFirst(city),
            }));
    }, [cities]);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Show loading state during SSR/initial render
    if (!isMounted) {
        return (
            <section className="bg-white" suppressHydrationWarning>
                <div className="flex flex-col gap-3" suppressHydrationWarning>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Mobile Number
                        </Text>
                        <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Name
                        </Text>
                        <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Date of Birth
                        </Text>
                        <div className="border border-gray-300 rounded-[4px] px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Location
                        </Text>
                        <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Do you want to port your policy?
                        </Text>
                        <div className="flex flex-row mt-4 gap-10 w-full">
                            <div className="border border-gray-300 rounded-md px-4 py-2 w-[50%] h-10"></div>
                            <div className="border border-gray-300 rounded-md px-4 py-2 w-[50%] h-10"></div>
                        </div>
                    </div>
                    <div className="w-full my-2">
                        <button
                            type="button"
                            disabled
                            className="bg-orange-300 text-white cursor-not-allowed rounded-md w-full px-10 py-3"
                        >
                            Get Quote
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-white">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0">
                    <Text
                        id={generateId("mobile-number-label", formName)}
                        htmlFor={generateId("mobile-number-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Mobile Number
                    </Text>
                    <PhoneInput
                        id={generateId("mobile-number-input", formName)}
                        isds={isds}
                        inputValue={getQuoteForm?.phoneNumber}
                        countryValue={getQuoteForm?.country || defaultCountry}
                        onMobileChange={onMobileChange}
                        onCountryChange={onCountryChange}
                        isMask={initData?.channel == "Tele Caller" || false}
                        onMobileReset={onMobileReset}
                        newDesignConfig={false}
                        isLeadForm
                    />

                    {errors.phone && (
                        <Text size="xs" color="red">
                            {errors.phone}
                        </Text>
                    )}
                </div>
                <div className="flex flex-col gap-0">
                    <Text
                        id={generateId("name-label", formName)}
                        htmlFor={generateId("name-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Name
                    </Text>
                    <TextInput
                        id={generateId("name-input", formName)}
                        placeholder="Enter your name"
                        size="sm"
                        radius="md"
                        className="rounded-md text-base"
                        classNames={classes}
                        value={getQuoteForm?.name}
                        onChange={onNameChange}
                        styles={{
                            input: {
                                outline: "none",
                                boxShadow: "none",
                                '&:focus': {
                                    outline: "none",
                                    boxShadow: "none",
                                },
                            },
                        }}
                    />
                    {errors.name && (
                        <Text size="xs" color="red">
                            {errors.name}
                        </Text>
                    )}
                </div>
                <div className="flex flex-col gap-0">
                    <Text
                        id={generateId("dob-label", formName)}
                        htmlFor={generateId("dob-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Date of Birth
                    </Text>
                    <div className="flex items-center gap-2 border border-gray-300 rounded-[4px] px-3 py-2.5 bg-white">
                        <input
                            id={generateId("dob-input", formName)}
                            type="date"
                            className="flex-1 text-md font-normal outline-none border-none bg-transparent text-base"
                            required
                            onChange={onDateChange}
                            value={getQuoteForm?.dob}
                            min={getNYearsBack(constraint.max).add(1, "day").format("YYYY-MM-DD")}
                            max={getNYearsBack(constraint.min).format("YYYY-MM-DD")}
                        />
                    </div>
                    {errors.dob && (
                        <Text size="xs" color="red">
                            {errors.dob}
                        </Text>
                    )}
                </div>
                <div className="flex flex-col gap-0">
                    <Text
                        id={generateId("location-label", formName)}
                        htmlFor={generateId("location-select", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Location
                    </Text>
                    <Select
                        id={generateId("location-select", formName)}
                        placeholder="Select City"
                        size="sm"
                        radius="md"
                        value={getQuoteForm?.city || ""}
                        data={capitalizedCityOptions}
                        onChange={(val) => onCityChange(val || "")}
                        nothingFoundMessage="No city found"
                        error={errors.city || undefined}
                        searchable
                        allowDeselect={false}
                        className="text-base"
                        classNames={classes}
                    />
                </div>
                <div className="flex flex-col gap-0">
                    <Radio.Group
                        id={generateId("port-policy-group", formName)}
                        label="Do you want to port your policy?"
                        value={getQuoteForm?.port || ""}
                        onChange={onPortChange}
                        error={errors.port || undefined}
                    >
                        <div className="flex flex-row mt-4 gap-10 w-full">
                            <Radio
                                id={generateId("port-policy-yes-radio", formName)}
                                value="Yes"
                                label="Yes"
                                variant="outline"
                                color="#FFAF14"
                                classNames={radioClasses}
                                className="rounded-md w-[50%] text-base"
                            />
                            <Radio
                                id={generateId("port-policy-no-radio", formName)}
                                value="No"
                                label="No"
                                variant="outline"
                                color="#FFAF14"
                                classNames={radioClasses}
                                className="rounded-md w-[50%] text-base"
                            />
                        </div>
                    </Radio.Group>
                </div>
                <LeadFormDisclaimer />

                <div className="w-full my-2">
                    <button
                        id={generateId("get-quote-button", formName)}
                        disabled={isFormInvalid() || isLoading}
                        type="submit"
                        aria-label="Get insurance quote"
                        className={`rounded-md w-full px-10 py-2 font-semibold transition-opacity ${isFormInvalid() || isLoading
                            ? "bg-orange-300 text-white cursor-not-allowed"
                            : "bg-nivabupa-ethinosHomeOrange text-white hover:opacity-90"}`}
                        onClick={onViewPlan}
                    >
                        {isLoading ? <Spinner /> : "Get Quote"}
                    </button>
                </div>
            </div>
        </section>
    );
}

export default FiveLeadForm;